import { forwardRef, useImperativeHandle, useState, type MouseEvent } from 'react';
import { Button, EditIcon, Flex, Text } from '@/uicomponents';
import { InlineEdit } from '../inline-edit';
import { Show } from '../show';

/** AC4 — one label for every surface and viewer type. */
export const DEAL_REFERENCE_LABEL = 'Deal Reference';
export const DEAL_REFERENCE_MAX_LENGTH = 255;

export interface DealReferenceRowHandle {
  enterEditMode: () => void;
}

export interface DealReferenceRowProps {
  value: string | null;
  /** Internal viewers can edit; externals get read-only (or nothing when empty). */
  isInternal: boolean;
  onSave?: (next: string | null) => void;
  /**
   * `inline` (default) — internal users get a click-to-edit pencil on the value
   * and an "Add Deal Reference" invite when empty. `kebab` — no inline
   * affordance at all (strict NPJB-539 AC2): the value is read-only, an empty
   * field renders nothing, and add/edit happens only via `enterEditMode()`
   * driven from a menu.
   */
  editAffordance?: 'inline' | 'kebab';
}

/**
 * The "Deal Reference" row — shared by the campaign card/detail (NPJB-420, inline
 * affordance) and the audience card (NPJB-539, kebab-only). States: hidden
 * (external + empty, or kebab-mode + empty), "Add Deal Reference" invite
 * (inline + internal + empty), an editable/read-only mono value, and the inline
 * editor. Also editable imperatively via `enterEditMode()`. Interactive parts
 * stop propagation so a clickable card never navigates on an edit gesture.
 */
export const DealReferenceRow = forwardRef<DealReferenceRowHandle, DealReferenceRowProps>(
  ({ value, isInternal, onSave, editAffordance = 'inline' }, ref) => {
    const [editing, setEditing] = useState(false);
    useImperativeHandle(ref, () => ({ enterEditMode: () => setEditing(true) }));

    // Editable = internal AND a save handler (callers omit it e.g. when the
    // record is archived — the row then renders read-only, or not at all).
    const canEdit = isInternal && Boolean(onSave);
    const inline = editAffordance === 'inline';
    const showEditor = canEdit && editing;
    // Empty + not editing renders nothing unless the inline invite can show.
    if (!value && !showEditor && !(canEdit && inline)) {
      return null;
    }

    const stop = (e: MouseEvent) => e.stopPropagation();
    const commit = (next: string) => {
      onSave?.(next.trim() || null);
    };

    const showValue = Boolean(value) && !showEditor;
    const showEditableValue = showValue && canEdit && inline;
    const showReadonlyValue = showValue && !showEditableValue;
    const showInvite = canEdit && !value && !showEditor && inline;

    return (
      <Flex align='center' gap={8}>
        <Text variant='eyebrow' tone='tertiary'>
          {DEAL_REFERENCE_LABEL}
        </Text>
        <Show when={showEditor}>
          <Flex flex='1' onClick={stop}>
            <InlineEdit
              value={value ?? ''}
              onCommit={commit}
              onDone={() => setEditing(false)}
              ariaLabel={DEAL_REFERENCE_LABEL}
              placeholder='e.g. IO-12345'
              maxLength={DEAL_REFERENCE_MAX_LENGTH}
              focusOnMount
            />
          </Flex>
        </Show>
        <Show when={showEditableValue}>
          <Button
            type='text'
            size='small'
            aria-label='Edit deal reference'
            onClick={(e) => {
              stop(e);
              setEditing(true);
            }}
          >
            <Text family='mono'>{value}</Text>
            <EditIcon />
          </Button>
        </Show>
        <Show when={showReadonlyValue}>
          <Text family='mono'>{value}</Text>
        </Show>
        <Show when={showInvite}>
          <Button
            type='link'
            size='small'
            onClick={(e) => {
              stop(e);
              setEditing(true);
            }}
          >
            Add {DEAL_REFERENCE_LABEL}
          </Button>
        </Show>
      </Flex>
    );
  },
);

DealReferenceRow.displayName = 'DealReferenceRow';
